import { ObjectComparer } from "../Utils/ObjectComparer";
import { ActionType } from "./ActionType";
import { ResponseState } from "./ResponseState";
import { BaseStore } from "./BaseStore";
import { Dispatcher } from "./Dispatcher";
import * as Arrays from "../Utils/Arrays";

export interface ICacheablePayloadAction<TPayload, TQuery> {
    actionType: ActionType;

    payload?: TPayload;

    query: TQuery;
}

export interface ICacheablePayloadStoreState<TPayload, TQuery> {
    cachedEntries?: ICachedEntry<TPayload, TQuery>[];
}

export interface ICachedEntry<TPayload, TQuery> {
    responseState: ResponseState;

    payload?: TPayload;

    query: TQuery;
    
    actionType: ActionType;
}

export class CacheablePayloadStore<TPayload, TQuery> extends BaseStore<ICacheablePayloadStoreState<TPayload, TQuery>> {
    constructor(dispatcher: Dispatcher<ICacheablePayloadAction<TPayload, TQuery>>) {
        super();

        this.subscribeToDispatcher(dispatcher, (action) => this.onDispatch(action));
    }

    protected getDefaultState(): ICacheablePayloadStoreState<TPayload, TQuery> {
        return { cachedEntries: [] };
    }

    public isLoading(): boolean {
        const entries = this.getState().cachedEntries;
        if (Arrays.isNullOrEmpty(entries)) {
            return false;
        }

        const loadingEntries = entries.filter((e) => e.responseState === ResponseState.Requested);

        return !Arrays.isNullOrEmpty(loadingEntries);
    }

    public isLoadingForQuery(query: TQuery): boolean {
        const entry = this.getEntry(query);

        return entry && entry.responseState === ResponseState.Requested;
    }

    public isEmpty(): boolean {
        const entries = this.getState().cachedEntries;

        return Arrays.isNullOrEmpty(entries);
    }

    public getAllPayload(): ICachedEntry<TPayload, TQuery>[] {
        return this.getState().cachedEntries || [];
    }

    public getPayload(query: TQuery): TPayload {
        const entry = this.getEntry(query);

        return entry ? entry.payload : null;
    }

    public isErrorForQuery(query: TQuery): boolean {
        const entry = this.getEntry(query);

        return entry && entry.actionType === ActionType.Error;
    }

    public isEmptyForQuery(query: TQuery): boolean {
        const entry = this.getEntry(query);

        return !entry || entry.responseState === ResponseState.Empty;
    }

    protected onDispatch(action: ICacheablePayloadAction<TPayload, TQuery>): void {
        let entries = this.getState().cachedEntries;

        switch (action.actionType) {
            case ActionType.Loading:
                this.editOrAddEntry(entries, action.query, ResponseState.Requested, action.actionType);
                break;
            case ActionType.Success:
                this.editOrAddEntry(entries, action.query, ResponseState.Received, action.actionType, action.payload);
                break;
            case ActionType.Error:
                this.editOrAddEntry(entries, action.query, ResponseState.Empty, action.actionType, action.payload);
                break;
            case ActionType.FlushStore:
                entries = null;
                break;
            default:
                throw new Error("CacheablePayloadStore<TPayload, TQuery>.onDispatch: Unexpected action type");
        }

        this.setState({ cachedEntries: null });
        this.setState({ cachedEntries: entries });
    }

    private editOrAddEntry(
        cachedEntries: ICachedEntry<TPayload, TQuery>[],
        query: TQuery,
        responseState: ResponseState,
        actionType: ActionType,
        payload?: TPayload
    ) {
        for (let i = 0; i < cachedEntries.length; i++) {
            if (ObjectComparer.isSame(cachedEntries[i].query, query)) {
                cachedEntries[i] = {
                    query: query,
                    payload: payload ? payload : cachedEntries[i].payload,
                    responseState: responseState,
                    actionType: actionType
                };

                return;
            }
        }

        cachedEntries.push({ query: query, payload: payload, responseState: responseState, actionType: actionType });
    }

    private getEntry(query: TQuery) {
        const entries = this.getState().cachedEntries;
        if (Arrays.isNullOrEmpty(entries)) {
            return null;
        }

        const filtredEntries = entries.filter((e) => ObjectComparer.isSame(e.query, query));

        return Arrays.isNullOrEmpty(filtredEntries) ? null : filtredEntries[0];
    }
}
